import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { SocialService } from '../../services/socialService';
import { useAuth } from '../../context/AuthContext';
import AuthModal from '../../components/AuthModal/AuthModal';
import { useAuthGate } from '../../hooks/useAuthGate';
import { AUTH_MESSAGES } from '../../utils/authMessages';
import LoadingSkeleton from '../../components/LoadingSkeleton/LoadingSkeleton';
import styles from './Following.module.css';
import { FoundingCollectorIcon } from '../../components/FoundingCollectorBadge/FoundingCollectorBadge';

function Following() {
    const { username: rawUsername } = useParams();
    const username = rawUsername?.startsWith('@') ? rawUsername.slice(1) : rawUsername;
    const { user } = useAuth();
    const { gateAction, authModalProps } = useAuthGate(user);
    const [following, setFollowing] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadFollowing = async () => {
            try {
                setLoading(true);
                setError(null);
                const profile = await SocialService.getPublicProfile(username);
                const data = await SocialService.getFollowing(profile.id);
                setFollowing(data);
            } catch (err) {
                console.error('Failed to load following:', err);
                setError(err.message || 'Failed to load following');
            } finally {
                setLoading(false);
            }
        };

        if (username) {
            loadFollowing();
        }
    }, [username]);

    const handleFollow = async (userId, currentlyFollowing) => {
        try {
            if (currentlyFollowing) {
                await SocialService.unfollow(userId);
            } else {
                await SocialService.follow(userId);
            }

            setFollowing(following.map(followedUser =>
                followedUser.id === userId
                    ? { ...followedUser, isFollowing: !currentlyFollowing }
                    : followedUser
            ));
        } catch (err) {
            console.error('Failed to update follow status:', err);
        }
    };

    if (loading) {
        return (
            <div className={styles.page}>
                <div className="container">
                    <LoadingSkeleton height="40px" width="200px" />
                    <div style={{ marginTop: 24 }}>
                        {[1, 2, 3].map(i => (
                            <div key={i} style={{ marginBottom: 16 }}>
                                <LoadingSkeleton height="64px" />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.page}>
                <div className="container">
                    <div className={styles.errorState}>
                        <p>{error}</p>
                        <Link to={`/user/${username}`} className={styles.backLink}>← Back to Profile</Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <main className={styles.page}>
            <div className="container">
                <div className={styles.header}>
                    <Link to={`/user/${username}`} className={styles.backLink}>← Back</Link>
                    <h1 className={styles.title}>Following</h1>
                    <p className={styles.subtitle}>@{username}</p>
                </div>

                {following.length === 0 ? (
                    <div className={styles.emptyState}>
                        <span style={{ fontSize: '2.5rem' }}>👥</span>
                        <p>Not following anyone yet</p>
                    </div>
                ) : (
                    <div className={styles.userList}>
                        {following.map(followedUser => (
                            <div key={followedUser.id} className={styles.userCard}>
                                <Link to={`/user/${followedUser.username}`} className={styles.userInfo}>
                                    {followedUser.avatar_url ? (
                                        <img
                                            src={followedUser.avatar_url}
                                            alt={followedUser.display_name}
                                            className={styles.avatar}
                                        />
                                    ) : (
                                        <div className={styles.avatarPlaceholder}>
                                            {(followedUser.display_name || followedUser.username).charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                    <div className={styles.userDetails}>
                                        <p className={styles.displayName}>
                                            {followedUser.display_name || followedUser.username}
                                            <FoundingCollectorIcon isFoundingCollector={followedUser.isFoundingCollector} />
                                        </p>
                                        <p className={styles.username}>@{followedUser.username}</p>
                                    </div>
                                </Link>
                                {user?.username !== followedUser.username && (
                                    <button
                                        className={`${styles.followBtn} ${followedUser.isFollowing ? styles.following : ''}`}
                                        onClick={gateAction(
                                            () => handleFollow(followedUser.id, followedUser.isFollowing),
                                            AUTH_MESSAGES.FOLLOW,
                                            {
                                                type: 'FOLLOW_USER',
                                                returnUrl: window.location.pathname + window.location.search + window.location.hash,
                                                userId: followedUser.id,
                                            },
                                        )}
                                    >
                                        {followedUser.isFollowing ? 'Following' : 'Follow'}
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <AuthModal {...authModalProps} />
        </main>
    );
}

export default Following;
